import React from "react"
import { type VariantProps } from "class-variance-authority"

import {
  Button,
  buttonVariants,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"

import { Permission, Workspace, Connection } from "@/types"
import { usePermissionCheck } from "@/hooks"

export interface PermissionButtonProps
  extends
    React.ComponentProps<typeof Button>,
    VariantProps<typeof buttonVariants> {
  /** The permission required to enable this button */
  permission: Permission
  /** Workspace to check permissions against */
  workspace?: Workspace
  /** Connection to check permissions against */
  connection?: Connection
  /** Tooltip message to show when user lacks permission */
  disabledTooltip?: string
}

/**
 * A Button that automatically handles permission checks.
 * When the user lacks the required permission:
 * - The button is disabled
 * - A tooltip shows the reason (if disabledTooltip is provided)
 *
 * Supports asChild for Radix Slot composition (e.g., wrapping a Link).
 */
const PermissionButton = React.forwardRef<
  React.ComponentRef<typeof Button>,
  PermissionButtonProps
>(
  (
    {
      permission,
      workspace,
      connection,
      disabledTooltip,
      disabled,
      children,
      asChild,
      ...buttonProps
    },
    ref
  ) => {
    const { hasPermission, isLoading } = usePermissionCheck({
      permission,
      workspace,
      connection,
    })

    const isDisabled = disabled || isLoading || !hasPermission

    // If disabled due to permission and has tooltip, wrap with Tooltip
    // Use span wrapper to enable tooltip on disabled button (disabled elements don't trigger hover events)
    if (isDisabled && disabledTooltip && !hasPermission) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-block">
              <Button {...buttonProps} ref={ref} disabled asChild={asChild}>
                {children}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{disabledTooltip}</TooltipContent>
        </Tooltip>
      )
    }

    // Normal button (enabled or disabled without tooltip)
    return (
      <Button
        {...buttonProps}
        ref={ref}
        disabled={isDisabled}
        asChild={asChild}
      >
        {children}
      </Button>
    )
  }
)

PermissionButton.displayName = "PermissionButton"

export default PermissionButton
